import { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CalendarOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import BlockedDatesList from './BlockedDatesList';
import BookingsWarningModal from './BookingsWarningModal';

const AvailabilityManager = ({ 
    blockedDates, 
    onBlockDate,
    onUnblockDate,
    onCancelBookings,
    existingBookingsWarning,
    setExistingBookingsWarning,
    loading
}) => {
    const [selectedBlockDate, setSelectedBlockDate] = useState('');
    const [blockEndDate, setBlockEndDate] = useState('');
    const [blockNote, setBlockNote] = useState('');
    const [isDateRange, setIsDateRange] = useState(false);
    const [blockingDate, setBlockingDate] = useState(false);

    const todayStr = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

    const handleBlockDate = async () => {
        if (!selectedBlockDate) {
            toast.error('Please select a date to block');
            return;
        }
        
        setBlockingDate(true);
        try {
            const endDate = isDateRange && blockEndDate ? blockEndDate : selectedBlockDate;
            await onBlockDate(selectedBlockDate, endDate, blockNote);
            setSelectedBlockDate('');
            setBlockEndDate('');
            setBlockNote('');
            setIsDateRange(false);
        } finally {
            setBlockingDate(false);
        }
    };

    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return format(new Date(dateStr), 'dd MMM yyyy');
        } catch (e) {
            return dateStr;
        }
    };

    const handleRangeToggle = (e) => {
        setIsDateRange(e.target.checked);
        if (!e.target.checked) setBlockEndDate('');
    };

    const showWarningModal = Boolean(existingBookingsWarning);

    return (
        <div className="grid md:grid-cols-2 gap-6">
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <CalendarOff className="w-5 h-5" />
                        Block Dates
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                        <input
                            type="checkbox"
                            id="date-range"
                            checked={isDateRange}
                            onChange={handleRangeToggle}
                            className="rounded"
                        />
                        <Label htmlFor="date-range">Block date range</Label>
                    </div>
                    
                    <div>
                        <Label htmlFor="start-date">{isDateRange ? 'Start Date' : 'Date to Block'}</Label>
                        <Input
                            id="start-date"
                            type="date"
                            min={todayStr}
                            value={selectedBlockDate}
                            onChange={(e) => setSelectedBlockDate(e.target.value)}
                            data-testid="block-date-input"
                        />
                    </div>

                    {isDateRange && (
                        <div>
                            <Label htmlFor="end-date">End Date</Label>
                            <Input
                                id="end-date"
                                type="date"
                                min={selectedBlockDate || todayStr}
                                value={blockEndDate}
                                onChange={(e) => setBlockEndDate(e.target.value)}
                                data-testid="block-end-date-input"
                            />
                        </div>
                    )}

                    {selectedBlockDate && (
                        <div className="bg-secondary/50 p-3 rounded-lg text-sm">
                            <strong>Selected:</strong> {formatDisplayDate(selectedBlockDate)}
                            {isDateRange && blockEndDate && (
                                <span> to {formatDisplayDate(blockEndDate)}</span>
                            )}
                        </div>
                    )}
                    
                    <div>
                        <Label htmlFor="block-note">Note (optional)</Label>
                        <Input
                            id="block-note"
                            value={blockNote}
                            onChange={(e) => setBlockNote(e.target.value)}
                            placeholder="e.g., Holiday, Private booking"
                            data-testid="block-note-input"
                        />
                    </div>
                    
                    <Button
                        className="w-full btn-primary"
                        onClick={handleBlockDate}
                        disabled={!selectedBlockDate || blockingDate}
                        data-testid="block-date-btn"
                    >
                        {blockingDate && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        {!blockingDate && <CalendarOff className="w-4 h-4 mr-2" />}
                        Block Date(s)
                    </Button>
                </CardContent>
            </Card>
            
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Blocked Dates</CardTitle>
                </CardHeader>
                <CardContent>
                    <BlockedDatesList 
                        blockedDates={blockedDates} 
                        onUnblockDate={onUnblockDate} 
                    />
                </CardContent>
            </Card>

            {showWarningModal && (
                <BookingsWarningModal
                    warning={existingBookingsWarning}
                    onClose={() => setExistingBookingsWarning(null)}
                    onCancelBookings={onCancelBookings}
                    loading={loading}
                />
            )}
        </div>
    );
};

export default AvailabilityManager;
